import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { HeroModele } from '@components/hero-modele/models';
import { PopinPriceDetailComponent } from '@components/popin-price-detail';
import { TranslatePipe } from '@ngx-translate/core';
import { ImageComponent } from '@shared/components/image';
import { PregenerationSize } from '@shared/components/image/models';
import { findServerCandidate, getCDNLink } from '@shared/components/image/utils';
import { SvgComponent } from '@shared/components/svg';
import { Kitchen, KitchenVariant } from '@shared/models';
import { TransformFirstLetterPipe } from '@shared/pipe';
import { DialogService } from '@shared/services/dialog';
import { ImageService } from '@shared/services/image';
import { PlatformContextService } from '@shared/services/platform-context';
import { UrlParamsService } from '@shared/services/url-params';
import { moveItemToFront, observeResize } from '@shared/utils';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';

@Component({
  selector: 'app-hero-modele',
  imports: [ImageComponent, SvgComponent, TranslatePipe, RouterLink, NgClass, TransformFirstLetterPipe],
  templateUrl: './hero-modele.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroModeleComponent extends AbstractDynamicComponent<HeroModele> implements OnDestroy {
  /** The visual's box on desktop: as wide as the window, `desk:h-[627px]` high. */
  protected readonly frame: PregenerationSize = { width: 1920, height: 627, exact: 'desk', fluid: true };
  readonly kitchenVariants = signal<KitchenVariant[]>(null!);
  readonly imageSelected = computed(() => this.data.kitchen.variants[this.variantSelected()]?.image);
  readonly variantIdSelected = computed(() => this.data.kitchen.variants[this.variantSelected()]?.id);
  protected readonly visualElement = viewChild<ElementRef<HTMLVideoElement>>('visual');
  private readonly dialogService = inject(DialogService);
  private readonly urlParamsService = inject(UrlParamsService);
  readonly variantSelected = computed<number>(() => {
    // Get the filter_front parameter value
    const filterLabel = this.urlParamsService.param('filter_front')();

    // Get the variants list
    const variants = this.data.kitchen.variants;

    // First check all fronts at index 0, then all at index 1, etc.
    const idx = variants.findIndex(
      (v) =>
        // Optional chaining to avoid errors if fronts is undefined
        v.name === filterLabel
    );

    if (idx >= 0) return idx;

    // If no match found, return 0 as default
    return 0;
  });
  private readonly imageService = inject(ImageService);
  private readonly isServer = inject(PlatformContextService).isServer;
  private readonly filterFront = this.urlParamsService.param('filter_front');

  constructor() {
    super();
    effect(() => {
      const front = untracked(this.filterFront);
      const variantSelected = untracked(this.variantSelected);
      if (front) {
        this.kitchenVariants.set(moveItemToFront(this.data.kitchen.variants, variantSelected));
      } else {
        this.kitchenVariants.set(this.data.kitchen.variants);
      }
    });
    effect((onCleanup) => {
      if (this.isServer()) return;

      const visualElement = this.visualElement();
      if (!visualElement) return;

      const el: Element = visualElement.nativeElement;
      const sub = observeResize(el)
        .pipe(
          map(({ contentRect }) => ({ w: contentRect.width, h: contentRect.height })),
          distinctUntilChanged((a, b) => a.w === b.w && a.h === b.h),
          debounceTime(300)
        )
        .subscribe(({ w, h }) => {
          // The file the visual asks for on a standard screen. Not the dense one on purpose: those
          // preloads would make a 2x page heavier than it was (DIGSOC-5268).
          const box = { width: Math.round(w), height: Math.round(h) };
          const size = findServerCandidate(this.frame, box) ?? box;
          const variants = this.data.kitchen.variants;
          for (const variant of variants) {
            // The shown variant's file is already on screen, at the screen's own density.
            if (variant.id === this.variantIdSelected()) continue;

            const cdnLink = getCDNLink(variant.image, size);
            if (cdnLink) {
              this.imageService.setImagePreload(cdnLink, variant.id);
            }
          }
        });

      onCleanup(() => sub.unsubscribe());
    });
  }

  ngOnDestroy(): void {
    this.imageService.clearAllImagePreloads();
  }

  openPriceDetailsDialog(): void {
    const kitchen: Kitchen = this.data.kitchen;
    this.dialogService.openDialog(PopinPriceDetailComponent, 'BIG', {
      data: {
        id: kitchen.id,
        shortRef: kitchen.implantation?.shortRef,
      },
    });
  }

  selectVariant(frontName: string): void {
    this.urlParamsService.mergeParams({ filter_front: frontName });
  }
}
